import { useEffect, useState } from "react";
import { getStats } from "../../services/adminService";
import {
  IconBuilding,
  IconCheckCircle,
  IconMail,
  IconStar
} from "../../components/Icons";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

function formatMonth(year, month) {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await getStats();
        setStats(data);
        setError(null);
      } catch (err) {
        setError("Failed to load dashboard stats. Please try again.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── LOADING ──────────────────────────────────────────────
  if (loading) {
    return (
      <div>
        <div className="dash-header">
          <h2>Dashboard</h2>
          <p className="dash-subtitle">Platform overview and activity summary</p>
        </div>
        <div className="loading-message">
          <span className="loading-spinner" />
          Loading dashboard...
        </div>
      </div>
    );
  }

  // ── ERROR ─────────────────────────────────────────────────
  if (error) {
    return (
      <div>
        <div className="dash-header">
          <h2>Dashboard</h2>
          <p className="dash-subtitle">Platform overview and activity summary</p>
        </div>
        <p className="admin-error">{error}</p>
      </div>
    );
  }

  const {
    totalBusinesses     = 0,
    verifiedBusinesses  = 0,
    unverifiedBusinesses = 0,
    contactMessages     = 0,
    categories          = [],
    cities              = [],
    monthlyRegistrations = []
  } = stats || {};

  return (
    <div>

      {/* ── HEADER ─────────────────────────────────────── */}
      <div className="dash-header">
        <h2>Dashboard</h2>
        <p className="dash-subtitle">Platform overview and activity summary</p>
      </div>

      {/* ── STAT CARDS ─────────────────────────────────── */}
      <div className="stat-grid">

        {/* Total Businesses */}
        <div className="admin-card stat-card stat-blue">
          <div className="stat-card-icon">
            <IconBuilding size={18} />
          </div>
          <h3>Total Businesses</h3>
          <p className="stat-number">{totalBusinesses}</p>
        </div>

        {/* Verified Businesses */}
        <div className="admin-card stat-card stat-green">
          <div className="stat-card-icon">
            <IconCheckCircle size={18} />
          </div>
          <h3>Verified</h3>
          <p className="stat-number">{verifiedBusinesses}</p>
        </div>

        {/* Unverified Businesses */}
        <div className="admin-card stat-card stat-amber">
          <div className="stat-card-icon">
            <IconStar size={18} />
          </div>
          <h3>Unverified</h3>
          <p className="stat-number">{unverifiedBusinesses}</p>
        </div>

        {/* Contact Messages */}
        <div className="admin-card stat-card stat-indigo">
          <div className="stat-card-icon">
            <IconMail size={18} />
          </div>
          <h3>Contact Messages</h3>
          <p className="stat-number">{contactMessages}</p>
        </div>

      </div>

      {/* ── CATEGORIES + CITIES (side by side) ─────────── */}
      <div className="analytics-row">

        {/* Business Categories */}
        <div>
          <h3 className="analytics-title">Business Categories</h3>
          {categories.length === 0 ? (
            <p className="analytics-empty">No category data available.</p>
          ) : (
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="col-count">Businesses</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, i) => (
                  <tr key={cat.name || i}>
                    <td>
                      <span className="analytics-rank">{i + 1}</span>
                      {cat.name || "Unknown"}
                    </td>
                    <td className="col-count">
                      <span className="analytics-count">{cat.count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top Cities */}
        <div>
          <h3 className="analytics-title">Top Cities</h3>
          {cities.length === 0 ? (
            <p className="analytics-empty">No city data available.</p>
          ) : (
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>City</th>
                  <th className="col-count">Businesses</th>
                </tr>
              </thead>
              <tbody>
                {cities.map((city, i) => (
                  <tr key={city.name || i}>
                    <td>
                      <span className="analytics-rank">{i + 1}</span>
                      {city.name || "Unknown"}
                    </td>
                    <td className="col-count">
                      <span className="analytics-count">{city.count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* ── MONTHLY REGISTRATIONS ───────────────────────── */}
      <div className="analytics-section">
        <h3 className="analytics-title">Monthly Registrations</h3>
        {monthlyRegistrations.length === 0 ? (
          <p className="analytics-empty">No registration data available.</p>
        ) : (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Month</th>
                <th className="col-count">New Businesses</th>
              </tr>
            </thead>
            <tbody>
              {/* Show most recent month first */}
              {[...monthlyRegistrations].reverse().map((entry, i) => (
                <tr key={`${entry.year}-${entry.month}`}>
                  <td>{formatMonth(entry.year, entry.month)}</td>
                  <td className="col-count">
                    <span className="analytics-count">{entry.count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default Dashboard;
